import type { AxiosInstance, AxiosResponse } from 'axios'
import axios from 'axios'
import { getToken } from '@/core/services/JwtService'
import { getApiBaseUrl } from '@/core/services/apiBaseUrl'
import { attachApiAxiosInterceptors } from '@/core/services/axiosAuthInterceptors'

/**
 * The PatientPortalApi client. One Axios instance, created by `init()` (main.ts) or on first use.
 * The exports match the React app's ApiService (`import * as ApiService`, `import { getClient }`)
 * so the API clients port unchanged.
 */

let axiosInstance: AxiosInstance | undefined

type HeaderBag = Record<string, string>

export function init(): void {
  axiosInstance = axios.create({
    baseURL: getApiBaseUrl(),
    // Fail a hung request instead of leaving it pending forever: a stalled save must surface as
    // an error the patient can see and retry, never appear saved when it isn't.
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' }
  })
  attachApiAxiosInterceptors(axiosInstance)
}

export function getClient(): AxiosInstance {
  if (!axiosInstance) init()
  return axiosInstance as AxiosInstance
}

function commonHeaders(): HeaderBag {
  return getClient().defaults.headers.common as unknown as HeaderBag
}

/** Puts the stored token (and `Accept: application/json`) on the instance defaults. */
function setHeader(): void {
  const token = getToken()
  if (!token) return
  const headers = commonHeaders()
  headers.Authorization = `bearer ${token}`
  headers.Accept = 'application/json'
}

export function query(resource: string, params: Record<string, unknown>): Promise<AxiosResponse> {
  setHeader()
  return getClient().get(resource, params)
}

export function get(resource: string): Promise<AxiosResponse> {
  setHeader()
  return getClient().get(resource)
}

export function getWithManualToken(resource: string, token: string): Promise<AxiosResponse> {
  commonHeaders().Authorization = `bearer ${token}`
  return getClient().get(resource)
}

export function post(
  resource: string,
  params: unknown,
  config?: { headers?: HeaderBag }
): Promise<AxiosResponse> {
  setHeader()
  return getClient().post(resource, params, config)
}

export function postWithManualToken(
  resource: string,
  params: unknown,
  bearerToken: string
): Promise<AxiosResponse> {
  commonHeaders().Authorization = `bearer ${bearerToken}`
  return getClient().post(resource, params)
}

export function update(resource: string, slug: string, params: unknown): Promise<AxiosResponse> {
  return getClient().put(`${resource}/${slug}`, params)
}

export function put(resource: string, params: unknown): Promise<AxiosResponse> {
  return getClient().put(resource, params)
}

export function del(resource: string): Promise<AxiosResponse> {
  return getClient().delete(resource)
}
